import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_HOST } from "../constants";
import { setTelegramUserInStorage } from "../utils/authStorage";
import type { TelegramUser } from "../types";

export const TelegramAuth = ({ onLogin }: { onLogin: (user: TelegramUser) => void }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<string>("Ожидание авторизации...");
  const [showRetry, setShowRetry] = useState(false);
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      alert("Токен авторизации не найден. Попробуйте снова.");
      navigate("/login", { replace: true });
      return;
    }

    // Периодически проверяем статус авторизации
    let consecutiveErrors = 0;
    const MAX_CONSECUTIVE_ERRORS = 5;
    const checkInterval = setInterval(async () => {
      try {
        const response = await fetch(`${API_HOST}/auth/check/${token}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) {
          throw new Error("Ошибка проверки статуса");
        }

        const data = await response.json();
        consecutiveErrors = 0;

        if (data.status === "authorized" && data.user_id) {
          // Авторизация успешна, получаем данные пользователя
          clearInterval(checkInterval);
          setStatus("Авторизация успешна! Загрузка данных...");

          try {
            // Получаем полные данные пользователя с прогрессом
            const userResponse = await fetch(`${API_HOST}/user/${data.user_id}`, {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            });

            if (!userResponse.ok) {
              throw new Error("Ошибка получения данных пользователя");
            }

            const userData = await userResponse.json();
            
            console.log("[TelegramAuth] User data received:", {
              id: userData.id,
              username: userData.username,
              first_name: userData.first_name,
              photo_url: userData.photo_url,
              progress_keys: Object.keys(userData.progress || {}),
            });
            
            // Сохраняем telegram_id для последующей проверки существования пользователя
            if (data.telegram_id) {
              localStorage.setItem("lastTelegramId", data.telegram_id);
            }
            
            // Формируем объект пользователя
            const user: TelegramUser = {
              id: userData.id,
              username: userData.username,
              first_name: userData.first_name,
              last_name: userData.last_name,
              photo_url: userData.photo_url,
              subscription_status: userData.subscription_status,
              progress: userData.progress || {},
            };

            setTelegramUserInStorage(JSON.stringify(user));
            localStorage.removeItem("authToken");
            onLogin(user);
            // В TG in-app browser SPA-навигация может не обновлять view — делаем жёсткий переход
            window.location.replace("/profile");
          } catch (err) {
            console.error(err);
            setStatus("Ошибка получения данных пользователя");
            setTimeout(() => {
              navigate("/login", { replace: true });
            }, 2000);
          }
        } else if (data.status === "expired") {
          clearInterval(checkInterval);
          setStatus("Ссылка авторизации истекла.");
          setShowRetry(true);
        } else if (data.status === "not_found") {
          clearInterval(checkInterval);
          setStatus("Ссылка авторизации не найдена.");
          setShowRetry(true);
        } else {
          // status === "pending" - продолжаем ждать
          setStatus("Ожидание подтверждения в Telegram...");
        }
      } catch (err) {
        console.error(err);
        consecutiveErrors++;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          clearInterval(checkInterval);
          setStatus("Сервер недоступен. Пожалуйста, попробуйте позже.");
          setShowRetry(true);
          return;
        }
        setStatus("Не удалось связаться с сервером. Повторная попытка...");
      }
    }, 2000); // Проверяем каждые 2 секунды

    // Таймаут на 30 минут (время жизни токена, must match backend expires_minutes=30)
    const timeout = setTimeout(() => {
      clearInterval(checkInterval);
      setStatus("Время ожидания истекло.");
      setShowRetry(true);
    }, 30 * 60 * 1000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(timeout);
    };
  }, [token, navigate, onLogin]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-neutral-light">
      <div className="bg-white rounded-lg shadow p-6 text-center max-w-md w-full mx-4">
        {!showRetry && (
          <div className="mb-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          </div>
        )}
        <p className="text-neutral-text font-body">{status}</p>
        {showRetry ? (
          <button
            onClick={() => navigate("/login", { replace: true })}
            className="mt-4 px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg"
          >
            Попробовать снова
          </button>
        ) : (
          <p className="text-neutral-text/70 text-sm font-body mt-4">
            Если бот не открылся автоматически, найдите его в Telegram и нажмите /start
          </p>
        )}
      </div>
    </div>
  );
};


